import React, {useEffect, useRef, useState} from "react";
import {Box, Chip, Stack} from "@mui/material"
import {IconButton, Typography} from "@mui/joy";
import {CloseRounded, FilterListRounded} from "@mui-symbols-material/w300";
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {Error, FilteredSearch, TDAForm, updateUrlParams} from "@dreebit-registry/vsm-microservice-components";
import {gql, useApolloClient, useQuery} from "@apollo/client";
import {sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import _ from "lodash";
import moment from "moment";
import {FilterDrawerStorageKey, loadFilterDrawerOpen, saveFilterDrawerOpen} from "../../utils/filterDrawerStorage";
import {acceptsIdList, extractValueLabels} from "../../utils/valueQueryLabels";

const isPlainObject = (v: unknown): v is Record<string, any> =>
    v != null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);

const isObjectAllEmpty = (v: Record<string, any>) =>
    Object.values(v).every(x => x == null || x === "");

interface Props {
    children: React.ReactNode;
    query: any;
    queryVariables?: Record<string, any>;
    attributesPath: string;
    urlParams: URLSearchParams;
    setUrlParams: (params: any) => void;
    searchParams: { query: string; fields: string[] };
    filterParams: Record<string, any>;
    setFilterParams: (v: Record<string, any>) => void;
    resetFilterParams?: () => void;
    onSearch?: (values: { query: string; fields: string[] }) => void;
    panelWidth?: number;
    extraHeaderActions?: React.ReactNode;
    onAttributesChange?: (attributes: any[]) => void;
    // Adjusts the loaded filter attributes, e.g. drops options the list doesn't offer.
    // Must be stable (module-level function).
    transformAttributes?: (attributes: any[]) => any[];
    chipLabelOverrides?: Record<string, string>;
    openStateStorageKey?: FilterDrawerStorageKey;
}

const ListWithFilterPanel = ({
                                 children,
                                 query,
                                 queryVariables,
                                 attributesPath,
                                 urlParams,
                                 setUrlParams,
                                 searchParams,
                                 filterParams,
                                 setFilterParams,
                                 resetFilterParams,
                                 onSearch,
                                 panelWidth = 280,
                                 extraHeaderActions,
                                 onAttributesChange,
                                 transformAttributes,
                                 chipLabelOverrides,
                                 openStateStorageKey,
                             }: Props) => {
    const {t} = useTranslation();
    const apollo = useApolloClient();
    const [valueLabelCache, setValueLabelCache] = useState<Record<string, Record<string, string>>>({});
    const [open, setOpen] = useState(() =>
        openStateStorageKey ? loadFilterDrawerOpen(openStateStorageKey) : false
    );
    const [attributes, setAttributes] = useState<any[]>([]);
    // Children use this to hold back a first request that would otherwise ignore the URL filters.
    const [attributesLoaded, setAttributesLoaded] = useState(false);

    const handleOpenChange = (value: boolean) => {
        setOpen(value);
        if (openStateStorageKey) {
            saveFilterDrawerOpen(openStateStorageKey, value);
        }
    };

    const upsertCache = (attrIndex: string, map: Record<string, string>) =>
        setValueLabelCache(prev => ({
            ...prev,
            [attrIndex]: { ...(prev[attrIndex] || {}), ...map }
        }));

    const {data, error, refetch} = useQuery(query, {
        variables: {filterPreset: filterParams, ...queryVariables},
    });

    useEffect(() => {
        if (!data && !error) return;
        if (data && _.get(data, attributesPath)) {
            const raw = _.get(data, attributesPath, []);
            // TDAForm groups attributes via `obj.categories ?? ['noCategory']`.
            // The `??` operator doesn't catch empty arrays, so attributes with
            // categories:[] are added to no group and are silently invisible.
            // Normalize empty arrays to null so they fall into 'noCategory'.
            const categorized = raw.map((a: any) => ({
                ...a,
                categories: a.categories?.length === 0 ? null : a.categories
            }));
            const normalized = transformAttributes ? transformAttributes(categorized) : categorized;
            setAttributes(normalized);
            onAttributesChange?.(normalized);
        }
        // Settled, not "successful": an empty result or a failed query must not block a child
        // that waits for the attributes forever. Flipped in the same effect as `setAttributes`
        // so children see both in one render — child effects run before this parent effect,
        // so deriving it from `data` alone would release them one render too early.
        setAttributesLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error]);

    // Refetch the attribute definitions when the active filter really changed. The mount run is
    // skipped (the useQuery above just fetched) and the comparison is by value, because the
    // URL→state sync hands us a new `filterParams` object on every URL write.
    const lastRefetchedFilterRef = useRef(filterParams);
    useEffect(() => {
        if (_.isEqual(lastRefetchedFilterRef.current, filterParams)) return;
        lastRefetchedFilterRef.current = filterParams;
        refetch();
    }, [filterParams, refetch]);

    const handleFilterChange = (index: string, value: any) => {
        if (isPlainObject(value) || Array.isArray(value)) {
            const next = {...filterParams};
            const nextUrl = new URLSearchParams(urlParams);
            const empty = Array.isArray(value) ? value.length === 0 : isObjectAllEmpty(value);
            if (empty) {
                delete next[index];
                nextUrl.delete(index);
            } else {
                next[index] = value;
                nextUrl.delete(index);
                nextUrl.set(index, JSON.stringify(value));
            }
            setFilterParams(next);
            setUrlParams(nextUrl);
            return;
        }
        const newParams = {...filterParams, [index]: value};
        setFilterParams(newParams);
        updateUrlParams(newParams, urlParams, setUrlParams);
    };

    const isEmptyFilterValue = (v: unknown) => {
        if (v == null) return true;
        if (typeof v === "string") return v.trim() === "";
        if (Array.isArray(v)) return v.length === 0;
        if (typeof v === "object") {
            const obj = v as Record<string, any>;
            if (Object.keys(obj).length === 0) return true;
            return isObjectAllEmpty(obj);
        }
        return false;
    };

    const findInOptionValues = (attr: any, v: string) =>
        attr?.optionValues?.find((o: any) => String(o.value) === String(v))?.name;

    const findInDataQueryResult = (attr: any, v: string) => {
        const dqc = attr?.dataLoaderConfig?.dataQueryConfig;
        if (!dqc) return undefined;
        const items = _.get(data, dqc.dataKeyPath, []);
        const hit = items?.find(
            (it: any) => String(_.get(it, dqc.valueKeyPath)) === String(v)
        );
        return hit ? _.get(hit, dqc.titleKeyPath) : undefined;
    };

    const fetchViaValueQuery = async (attr: any, ids: string[]) => {
        const vqc = attr?.dataLoaderConfig?.valueQueryConfig;
        if (!vqc?.query) return;
        const QUERY = gql`${vqc.query}`;
        // List queries (`$id: [ID!]`) resolve every id in one request, single-id queries one by one
        const requests: Array<string | string[]> = acceptsIdList(QUERY) ? [ids] : ids;
        const results: Record<string, string> = {};
        for (const id of requests) {
            try {
                const res = await apollo.query({
                    query: QUERY,
                    variables: {id},
                    fetchPolicy: "network-only",
                });
                Object.assign(results, extractValueLabels(res.data, vqc));
            } catch {
            }
        }
        if (Object.keys(results).length) {
            upsertCache(attr.index, results);
        }
    };

    useEffect(() => {
        const tasks: Array<Promise<void>> = [];
        Object.entries(filterParams).forEach(([key, rawVal]) => {
            if (rawVal == null || rawVal === "" || (Array.isArray(rawVal) && rawVal.length === 0)) return;
            const attr = attributes.find(a => a.index === key);
            if (!attr) return;
            const vqc = attr?.dataLoaderConfig?.valueQueryConfig;
            if (!vqc?.query) return;
            const ensureArray = Array.isArray(rawVal) ? rawVal : [rawVal];
            const unresolved = ensureArray.filter(v => {
                const sv = String(v);
                const cached = valueLabelCache[key]?.[sv];
                const local = findInOptionValues(attr, sv) || findInDataQueryResult(attr, sv);
                return !cached && !local;
            }).map(String);
            if (unresolved.length) {
                tasks.push(fetchViaValueQuery(attr, unresolved));
            }
        });
        if (tasks.length) Promise.allSettled(tasks);
    }, [attributes, filterParams, data]);

    const formatFilterChipLabel = (key: string, value: any) => {
        if (chipLabelOverrides?.[key]) return chipLabelOverrides[key];
        const attr = attributes.find((a) => a.index === key);

        const resolveOne = (v: any) => {
            const sv = String(v);
            return (
                valueLabelCache[key]?.[sv] ||
                findInOptionValues(attr, sv) ||
                findInDataQueryResult(attr, sv) ||
                sv
            );
        };

        // Sonderfall: Object-Werte (z. B. dateRange { after, before })
        if (isPlainObject(value)) {
            const label = attr?.name ?? key;
            if ("after" in value || "before" in value) {
                const fmt = (d: any) => (d ? moment(d).format("L") : "…");
                return `${label}: ${fmt(value.after)} – ${fmt(value.before)}`;
            }
            const parts = Object.values(value).filter(v => v != null && v !== "");
            if (parts.length === 0) return "";
            return `${label}: ${parts.join(" – ")}`;
        }

        const display: string = Array.isArray(value)
            ? value.map(resolveOne).join(", ")
            : resolveOne(value);

        // Sonderfall: Boolean-Werte (Checkbox-Filter)
        if (typeof value === "boolean" || display.toLowerCase() === "true" || display.toLowerCase() === "false") {
            const isTrue =
                value === true ||
                display.toLowerCase() === "true";
            const label = attr?.name ?? key;

            // Nur anzeigen, wenn aktiv (true)
            return isTrue ? label : "";
        }

        // Normalfall
        return String(display);
    };

    const searchAttribute = attributes.find((a) => a.index === "search");

    // Once the search TDA loads, reconcile the active search fields against it:
    // strip values no longer offered (e.g. renamed/removed enum values) and, when nothing
    // valid remains, fall back to the TDA default selection (addOnAttribute.value).
    useEffect(() => {
        if (!searchAttribute) return;
        const validValues = (searchAttribute.addOnAttribute?.optionValues ?? []).map((o: any) => o.value);
        const defaults = (searchAttribute.addOnAttribute?.value ?? []).filter((v: string) => validValues.includes(v));
        const current = searchParams?.fields ?? [];
        const sanitized = current.filter((f: string) => validValues.includes(f));
        const next = sanitized.length ? sanitized : defaults;
        if (!_.isEqual(next, current)) {
            onSearch?.({query: searchParams?.query ?? "", fields: next});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAttribute]);

    const filterAttributes = attributes
        .filter((a) => a.index !== "search")
        .map(attr => ({
            ...attr,
            // TDAForm uses `categories ?? ['noCategory']` — an empty array [] is truthy,
            // causing forEach to run 0 times and the attribute to not render. Normalize to null.
            categories: attr.categories?.length ? attr.categories : null,
        }));

    // --- Chips Overflow ---
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleChips, setVisibleChips] = useState<[string, any][]>([]);
    const [overflowCount, setOverflowCount] = useState(0);

    useEffect(() => {
        const all = Object.entries(filterParams).filter(([_, v]) => {
            // false-Booleans sollen keine Chips haben
            if (typeof v === "boolean") return v === true;
            return !isEmptyFilterValue(v);
        });
        const update = () => {
            const container = containerRef.current;
            if (!container) return;

            // Temporär alle sichtbar machen
            const chipEls = Array.from(container.children) as HTMLElement[];
            chipEls.forEach(el => (el.style.display = "inline-flex"));

            let totalWidth = 0;
            let visibleCount = all.length;

            for (let i = 0; i < chipEls.length; i++) {
                const chip = chipEls[i];
                totalWidth += chip.offsetWidth + 8; // inkl. gap
                if (totalWidth > container.clientWidth - 40) { // Platz lassen für "+x"
                    visibleCount = i;
                    break;
                }
            }

            setVisibleChips(all.slice(0, visibleCount));
            setOverflowCount(all.length - visibleCount);
        };

        const observer = new ResizeObserver(update);
        if (containerRef.current) observer.observe(containerRef.current);
        update();

        return () => observer.disconnect();
    }, [filterParams]);
    // -----------------------

    return (
        <Box sx={{display: "flex", width: "100%", height: "100%", overflow: "hidden"}}>
            {/* Sidebar links */}
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key="filter-panel"
                        initial={{width: 0, opacity: 0}}
                        animate={{width: panelWidth, opacity: 1}}
                        exit={{width: 0, opacity: 0}}
                        transition={{duration: 0.3, ease: "easeInOut"}}
                        style={{
                            overflow: "hidden",
                            borderRight: "1px solid var(--joy-palette-neutral-outlinedBorder)",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: "14px 16px 14px 4px",
                            }}
                        >
                            <Box display="flex" alignItems="center">
                                <FilterListRounded/>
                                <Typography level={"titleMedium"} sx={{pl: "8px"}}>
                                    {t("Filter")}
                                </Typography>
                            </Box>
                            <IconButton
                                variant="outlined"
                                color="neutral"
                                size="sm"
                                onClick={() => handleOpenChange(false)}
                            >
                                <CloseRounded/>
                            </IconButton>
                        </Box>

                        <Box sx={{p: "0 16px 16px 4px", flex: 1, overflowY: 'auto'}}>
                            {error ? (
                                <Error errorObject={sanitizeErrorForDisplay(error, t)}/>
                            ) : (
                                <TDAForm
                                    attributes={filterAttributes}
                                    submitButtonText={t("Reset filters")}
                                    onReset={resetFilterParams}
                                    onChange={handleFilterChange}
                                    onValuesChange={(values: Record<string, any>) => {
                                        // TDAForm only fires `onChange` for primitive/array fields.
                                        // Complex inputs like the DateRangePicker only surface via
                                        // `onValuesChange`, so reconcile object-shaped values here.
                                        Object.entries(values).forEach(([key, val]) => {
                                            if (!isPlainObject(val)) return;
                                            if (_.isEqual(filterParams[key], val)) return;
                                            handleFilterChange(key, val);
                                        });
                                        // Also detect object filters that were cleared in the form
                                        Object.entries(filterParams).forEach(([key, val]) => {
                                            if (!isPlainObject(val)) return;
                                            if (!(key in values) || values[key] == null) {
                                                handleFilterChange(key, {});
                                            }
                                        });
                                    }}
                                    layout={"column"}
                                    initialFormValues={filterParams}
                                    searchParams={searchParams}
                                    onSearch={onSearch}
                                    resetButtonSxStyles={{width: "100%", mt: "5px"}}
                                />
                            )}
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Rechte Seite mit Searchbar + Tabelle */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    transition: "margin-left 0.3s ease",
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        p: 1.5,
                        flexShrink: 0,
                    }}
                >
                    {!open && (
                        <IconButton
                            variant="outlined"
                            color="neutral"
                            onClick={() => handleOpenChange(true)}
                            sx={{mr: "5px"}}
                        >
                            <FilterListRounded/>
                        </IconButton>
                    )}

                    {searchAttribute && (
                        <FilteredSearch
                            fields={searchAttribute.addOnAttribute?.optionValues}
                            value={searchParams?.query}
                            defaultFields={
                                searchParams?.fields?.length
                                    ? searchParams.fields
                                    : searchAttribute.addOnAttribute?.value || []
                            }
                            onSearch={(values) => onSearch?.(values)}
                        />
                    )}

                    {/* Dynamische Filter Chips */}
                    <Stack
                        ref={containerRef}
                        direction="row"
                        gap={1}
                        flexWrap="nowrap"
                        justifyContent="flex-start"
                        alignItems="center"
                        sx={{pl: "5px", overflow: "hidden", flex: 1}}
                    >
                        {visibleChips.map(([key, v]) => {
                            const label = formatFilterChipLabel(key, v);
                            if (!label) return null;

                            return <Chip
                                key={key}
                                size="medium"
                                variant="filled"
                                label={
                                    <Box
                                        sx={{
                                            maxWidth: 160,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                        title={label}
                                    >
                                        {label}
                                    </Box>
                                }
                                onDelete={() => {
                                    const next = {...filterParams};
                                    delete next[key];
                                    setFilterParams(next);
                                    const newUrl = new URLSearchParams(urlParams);
                                    newUrl.delete(key);
                                    setUrlParams(newUrl);
                                }}
                                deleteIcon={<CloseRounded sx={{color: "#333 !important"}}/>}
                            />
                        })}

                        {overflowCount > 0 && (
                            <Chip
                                size="medium"
                                variant="outlined"
                                label={`+${overflowCount}`}
                                sx={{
                                    backgroundColor: "var(--joy-palette-neutral-100)",
                                    color: "var(--joy-palette-neutral-700)",
                                    fontWeight: 500,
                                    height: 28,
                                }}
                            />
                        )}
                    </Stack>

                    {extraHeaderActions}
                </Stack>

                <Box sx={{flex: 1, overflow: "auto"}}>
                    {React.Children.map(children, (child) => {
                        if (React.isValidElement(child) && child.type !== React.Fragment) {
                            return React.cloneElement(child, {
                                attributes: attributes,
                                attributesLoaded: attributesLoaded,
                            } as any);
                        }
                        return child;
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default ListWithFilterPanel;
